import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Calendar, Bell, MessageSquare, Copy, ExternalLink, Download, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import ActionButton from '@/components/ActionButton';

export default function Analysis({ darkMode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analysisData, setAnalysisData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAnalysis();
  }, [id]);

  const loadAnalysis = () => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      const history = JSON.parse(localStorage.getItem('nowwhat_history') || '[]');
      const item = history.find(h => h.id === parseInt(id));
      
      if (item) {
        // Generate mock analysis based on content
        const analysis = generateMockAnalysis(item);
        setAnalysisData(analysis);
      }
      setIsLoading(false);
    }, 800);
  };

  const generateMockAnalysis = (item) => {
    // Mock AI analysis
    const analyses = {
      bill: {
        title: 'Utility Bill - Payment Due',
        summary: 'This is your monthly electricity bill from PowerCo. Your current balance is $127.43, which is slightly higher than last month due to increased usage.',
        keyPoints: [
          'Due date: April 15, 2026',
          'Amount due: $127.43',
          'Previous balance: $98.21',
          'Late fee after due date: $15.00'
        ],
        nextAction: {
          type: 'payment',
          title: 'Pay Bill',
          description: 'Set up payment before April 15 to avoid late fees',
          priority: 'high'
        },
        suggestedActions: [
          { icon: Calendar, label: 'Set Reminder', description: 'April 13, 2026' },
          { icon: Copy, label: 'Copy Amount', description: '$127.43' },
          { icon: ExternalLink, label: 'Open Payment Portal', description: 'Pay online' }
        ]
      },
      email: {
        title: 'Professional Email - Response Needed',
        summary: 'This email is from your colleague asking for feedback on the Q2 project proposal by end of week. The tone is professional but urgent.',
        keyPoints: [
          'Sender: Sarah Johnson (colleague)',
          'Subject: Q2 Project Proposal Feedback',
          'Deadline: Friday, 5 PM',
          'Action required: Review and provide feedback'
        ],
        nextAction: {
          type: 'reply',
          title: 'Draft Reply',
          description: 'Acknowledge receipt and confirm timeline',
          priority: 'medium'
        },
        suggestedActions: [
          { icon: MessageSquare, label: 'Draft Reply', description: 'Use suggested template' },
          { icon: Calendar, label: 'Add to Calendar', description: 'Friday deadline' },
          { icon: Copy, label: 'Copy Email', description: 'For reference' }
        ],
        draftReply: `Hi Sarah,\n\nThank you for sending over the Q2 proposal. I've received it and will review it thoroughly.\n\nI'll have my feedback to you by Thursday afternoon so you have time to incorporate any changes before the Friday deadline.\n\nLet me know if you need anything else in the meantime.\n\nBest regards`
      },
      general: {
        title: 'Content Analysis',
        summary: `I've analyzed your content and identified the key information and action items.`,
        keyPoints: [
          'Content type identified',
          'Main topics extracted',
          'Action items highlighted',
          'Priority level assessed'
        ],
        nextAction: {
          type: 'review',
          title: 'Review Details',
          description: 'Check the analysis below for next steps',
          priority: 'low'
        },
        suggestedActions: [
          { icon: Copy, label: 'Copy Summary', description: 'Save for later' },
          { icon: Download, label: 'Export', description: 'Download as PDF' }
        ]
      }
    };

    // Determine which template to use
    const content = item.extractedText || (typeof item.content === 'string' ? item.content.toLowerCase() : '');
    let analysis;
    
    if (content.includes('bill') || content.includes('payment') || content.includes('invoice')) {
      analysis = { ...analyses.bill };
    } else if (content.includes('email') || content.includes('message')) {
      analysis = { ...analyses.email };
    } else {
      analysis = { ...analyses.general };
    }
    
    // Add file-specific data
    return {
      ...analysis,
      originalContent: item.content,
      extractedText: item.extractedText,
      fileUrl: item.fileUrl,
      fileName: item.fileName,
      contentType: item.contentType
    };
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      case 'medium':
        return 'bg-warning/10 text-warning-foreground border-warning/20';
      case 'low':
        return 'bg-success/10 text-success border-success/20';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const handleCopyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="w-12 h-12 mx-auto mb-4 text-primary animate-spin" />
          <p className="text-muted-foreground">Analyzing...</p>
        </div>
      </div>
    );
  }

  if (!analysisData) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <h3 className="text-lg font-semibold text-foreground mb-2">Item not found</h3>
          <p className="text-muted-foreground mb-6">This analysis may have been deleted</p>
          <Button onClick={() => navigate('/')}>Return Home</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/history')}
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-foreground">Analysis Result</h1>
              <p className="text-xs text-muted-foreground">Clear explanation & next action</p>
            </div>
            <Badge className={getPriorityColor(analysisData.nextAction.priority)}>
              {analysisData.nextAction.priority} priority
            </Badge>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl pb-24">
        {/* Title */}
        <div className="mb-8 animate-slide-up">
          <h2 className="text-3xl font-bold text-foreground mb-2">{analysisData.title}</h2>
        </div>

        {/* Summary */}
        <Card className="p-6 mb-6 shadow-md border-l-4 border-l-primary">
          <div className="flex items-start gap-3 mb-3">
            <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
            <h3 className="font-semibold text-foreground">What this is</h3>
          </div>
          <p className="text-foreground leading-relaxed">{analysisData.summary}</p>
        </Card>

        {/* Key Points */}
        <Card className="p-6 mb-6 shadow-md">
          <h3 className="font-semibold text-foreground mb-4">Key Information</h3>
          <ul className="space-y-3">
            {analysisData.keyPoints.map((point, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                <span className="text-foreground">{point}</span>
              </li>
            ))}
          </ul>
        </Card>

        {/* Draft Reply if available */}
        {analysisData.draftReply && (
          <Card className="p-6 mb-6 shadow-md bg-accent-light">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">Suggested Reply</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCopyText(analysisData.draftReply)}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>
            <div className="bg-background rounded-lg p-4 whitespace-pre-wrap text-sm text-foreground font-mono">
              {analysisData.draftReply}
            </div>
          </Card>
        )}

        {/* Next Action */}
        <Card className="p-8 mb-6 shadow-lg bg-gradient-primary text-primary-foreground">
          <div className="text-center">
            <Sparkles className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">{analysisData.nextAction.title}</h3>
            <p className="text-primary-foreground/90 mb-6">{analysisData.nextAction.description}</p>
            <Button
              size="lg"
              variant="secondary"
              className="bg-background text-foreground hover:bg-background/90 shadow-lg"
              onClick={() => toast.success('Action initiated!')}
            >
              <CheckCircle2 className="w-5 h-5 mr-2" />
              Take Action Now
            </Button>
          </div>
        </Card>

        {/* Suggested Actions */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-foreground mb-4">Other Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {analysisData.suggestedActions.map((action, index) => (
              <ActionButton
                key={index}
                icon={action.icon}
                label={action.label}
                description={action.description}
                onClick={() => toast.success(`${action.label} initiated!`)}
              />
            ))}
          </div>
        </div>

        {/* Uploaded File Display */}
        {analysisData.fileUrl && (
          <Card className="p-6 mb-6 shadow-md">
            <h3 className="font-semibold text-foreground mb-4">Uploaded File</h3>
            {analysisData.contentType && analysisData.contentType.startsWith('image/') ? (
              <div className="bg-muted/30 rounded-lg p-4">
                <img 
                  src={analysisData.fileUrl} 
                  alt={analysisData.fileName || 'Uploaded file'}
                  className="max-w-full h-auto rounded-lg shadow-md mx-auto"
                  style={{ maxHeight: '500px' }}
                />
                <p className="text-sm text-muted-foreground mt-3 text-center">
                  {analysisData.fileName}
                </p>
              </div>
            ) : (
              <div className="bg-muted/30 rounded-lg p-4 flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Download className="w-8 h-8 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-foreground">{analysisData.fileName}</p>
                  <p className="text-sm text-muted-foreground">
                    {analysisData.contentType || 'Document'}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(analysisData.fileUrl, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* Extracted Text */}
        {analysisData.extractedText && (
          <Card className="p-6 mb-6 shadow-md bg-accent-light">
            <h3 className="font-semibold text-foreground mb-4">Extracted Text</h3>
            <div className="bg-background rounded-lg p-4 text-sm text-foreground whitespace-pre-wrap max-h-64 overflow-y-auto">
              {analysisData.extractedText}
            </div>
          </Card>
        )}

        {/* Original Content */}
        <Card className="p-6 shadow-md bg-muted/30">
          <h3 className="font-semibold text-foreground mb-4">Original Content</h3>
          <div className="bg-background rounded-lg p-4 text-sm text-muted-foreground whitespace-pre-wrap max-h-64 overflow-y-auto">
            {typeof analysisData.originalContent === 'string'
              ? analysisData.originalContent
              : 'Uploaded file content'}
          </div>
        </Card>
      </main>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-lg border-t border-border p-4">
        <div className="container mx-auto max-w-4xl flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => navigate('/')}
          >
            Analyze Another
          </Button>
          <Button
            className="flex-1 bg-primary hover:bg-primary-dark text-primary-foreground"
            onClick={() => toast.success('Marked as complete!')}
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Mark Complete
          </Button>
        </div>
      </div>
    </div>
  );
}
